/**
 * AccountantAnomalyReview - Standalone anomaly review page for accountants
 *
 * Fetches anomaly data from the dashboard API and renders the AnomalyResolver
 * component in an accountant-appropriate context (not inside the wizard).
 *
 * Accountant-specific features:
 * - Approve / reject existing resolutions
 * - Add forensic notes
 * - Flag anomalies for follow-up
 * - Read-only on underlying transaction data
 *
 * Route: /accountant/anomalies/:reportId
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAccountantAuth } from '../contexts/AccountantAuthContext';
import { useAuth } from '../contexts/TestAuthContext';
import { api } from '../utils/api';
import AccountantLayout from '../components/AccountantLayout';
import AnomalyResolver from '../components/wizard/AnomalyResolver';
import {
  ArrowLeft,
  Loader2,
  AlertTriangle,
  ShieldCheck,
  CheckCircle,
  XCircle,
  MessageSquare,
  Flag,
  RefreshCw,
} from 'lucide-react';

interface AccountantAction {
  anomaly_id: string;
  action_type: 'approve_resolution' | 'reject_resolution' | 'add_note' | 'flag';
  note: string;
  performed_by_name?: string;
  performed_at?: string;
}

interface AnomalyReviewData {
  success: boolean;
  report_id: string;
  business_name: string;
  anomalies: any[];
  periods_data: { month: string; revenue: number }[];
  total_transactions: number;
  total_months: number;
  unresolved_count: number;
  high_priority_count: number;
  resolved_count: number;
  loss_event_start?: string;
  loss_event_end?: string;
  has_cached_analysis: boolean;
  has_periods: boolean;
  error?: string;
}

export default function AccountantAnomalyReview() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const { accountant, loading: authLoading, getAuthHeader } = useAccountantAuth();
  const { user, getToken } = useAuth();

  // Dual-auth: support both AccountantAuth (external) and Clerk/TestAuth (internal)
  const isTestMode = process.env.REACT_APP_DISABLE_AUTH === 'true';

  const testAccountant = useMemo(() => ({
    id: 'test_user_123',
    email: 'test@claimsync.com',
    full_name: 'Test Accountant'
  }), []);

  const clerkAccountant = useMemo(() => user ? ({
    id: user.sub || user.userId || user.id || 'clerk-user',
    email: user.email || '',
    full_name: user.name || user.fullName || user.email || 'Staff'
  }) : null, [user]);

  const effectiveAccountant = isTestMode ? testAccountant : (accountant || clerkAccountant);
  const effectiveAuthLoading = isTestMode ? false : (authLoading && !user);

  const [reviewData, setReviewData] = useState<AnomalyReviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLog, setActionLog] = useState<AccountantAction[]>([]);
  const [savingAction, setSavingAction] = useState<string | null>(null);

  // Build auth headers supporting both auth contexts
  const getHeaders = useCallback(async () => {
    // Try AccountantAuth first (external accountant JWT)
    const accountantHeader = getAuthHeader();
    if (accountantHeader && (accountantHeader as any).Authorization) {
      return accountantHeader;
    }
    // Fall back to Clerk/TestAuth token
    try {
      const token = await getToken();
      if (token) return { Authorization: `Bearer ${token}` };
    } catch {}
    return {};
  }, [getAuthHeader, getToken]);

  const fetchReviewData = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    setError(null);
    try {
      const headers = await getHeaders();
      const data = await api.get<AnomalyReviewData>(
        `/api/v1/dashboard/anomaly-review/${reportId}`,
        { headers, showErrorToast: false }
      );
      if (data.success === false && data.error) {
        setError(data.error);
      }
      setReviewData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load anomaly data');
    } finally {
      setLoading(false);
    }
  }, [reportId, getHeaders]);

  useEffect(() => {
    if (effectiveAuthLoading) return;
    if (!effectiveAccountant) {
      navigate('/accountant/login');
      return;
    }
    fetchReviewData();
  }, [fetchReviewData, effectiveAccountant, effectiveAuthLoading]);

  const saveAccountantAction = async (
    anomalyId: string,
    actionType: AccountantAction['action_type'],
    note: string
  ) => {
    if (!reportId) return;
    setSavingAction(anomalyId);
    try {
      const headers = await getHeaders();
      await api.post(
        `/api/v1/dashboard/anomaly-review/${reportId}/accountant-action`,
        { anomaly_id: anomalyId, action_type: actionType, note },
        { headers, showErrorToast: false }
      );
      setActionLog(prev => [...prev, {
        anomaly_id: anomalyId,
        action_type: actionType,
        note,
        performed_at: new Date().toISOString(),
      }]);
      // Refresh data to reflect changes
      await fetchReviewData();
    } catch (err) {
      console.error('Failed to save action:', err);
    } finally {
      setSavingAction(null);
    }
  };

  // Handlers for AnomalyResolver callbacks
  const handleResolve = (anomalyId: string, reason: string, note: string) => {
    // Accountant resolving = approving with their reason
    saveAccountantAction(anomalyId, 'approve_resolution', `${reason}: ${note}`);
  };

  const handleBatchResolve = (anomalyIds: string[], reason: string, note: string) => {
    anomalyIds.forEach(id => {
      saveAccountantAction(id, 'approve_resolution', `${reason}: ${note}`);
    });
  };

  const getActionCountForAnomaly = (anomalyId: string) =>
    actionLog.filter(a => a.anomaly_id === anomalyId).length;

  // Summary stats
  const totalAnomalies = reviewData?.anomalies?.length || 0;
  const unresolvedCount = reviewData?.unresolved_count || 0;
  const resolvedCount = reviewData?.resolved_count || 0;
  const approvedCount = reviewData?.anomalies?.filter(
    (a: any) => a.accountant_approved || actionLog.some(
      act => act.anomaly_id === a.id && act.action_type === 'approve_resolution'
    )
  ).length || 0;

  if (effectiveAuthLoading) {
    return (
      <AccountantLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
            <p className="text-slate-500">Authenticating...</p>
          </div>
        </div>
      </AccountantLayout>
    );
  }

  return (
    <AccountantLayout>
      <div className="p-6 lg:p-8 max-w-[1200px] mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            title="Back"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
              <span className="font-semibold tracking-wide">ACCOUNTANT REVIEW</span>
              <span>•</span>
              <span>ANOMALY ANALYSIS</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900">
              {reviewData?.business_name || 'Anomaly Review'}
            </h1>
          </div>
          <button
            onClick={fetchReviewData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Status Summary Bar */}
        {reviewData && !loading && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="text-xl font-bold text-slate-900">{totalAnomalies}</p>
                  <p className="text-xs text-slate-500">Total Anomalies</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-500" />
                <div>
                  <p className="text-xl font-bold text-slate-900">{unresolvedCount}</p>
                  <p className="text-xs text-slate-500">Unresolved</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="text-xl font-bold text-slate-900">{resolvedCount}</p>
                  <p className="text-xs text-slate-500">Resolved</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="text-xl font-bold text-slate-900">{approvedCount}</p>
                  <p className="text-xs text-slate-500">Approved</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        {loading ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center shadow-sm">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-3" />
            <p className="text-slate-600 font-medium">Loading anomaly data...</p>
            <p className="text-slate-400 text-sm mt-1">Fetching anomalies and resolution history</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-xl border border-red-200 p-12 text-center shadow-sm">
            <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <p className="text-slate-900 font-medium">Failed to load anomaly data</p>
            <p className="text-slate-500 text-sm mt-1">{error}</p>
            <button
              onClick={fetchReviewData}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        ) : reviewData && totalAnomalies === 0 && !reviewData.has_cached_analysis ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center shadow-sm">
            <ShieldCheck className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <p className="text-slate-900 font-medium text-lg">No Anomalies Detected</p>
            <p className="text-slate-500 text-sm mt-1">
              {reviewData.has_periods
                ? 'Anomaly detection has not been run on this case yet. The data specialist needs to complete Step 3 of the wizard first.'
                : 'No financial data is available for anomaly detection.'}
            </p>
            <button
              onClick={() => navigate(-1)}
              className="mt-4 px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200"
            >
              Back to Dashboard
            </button>
          </div>
        ) : reviewData ? (
          <div className="space-y-6">
            {/* Accountant Action Quick Bar */}
            {unresolvedCount > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-4">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-amber-900 font-medium">
                    {unresolvedCount} anomal{unresolvedCount === 1 ? 'y' : 'ies'} pending review
                  </p>
                  <p className="text-amber-700 text-sm">
                    Review each anomaly and either approve the resolution, add forensic notes, or flag for follow-up.
                  </p>
                </div>
              </div>
            )}

            {/* Session Action Log */}
            {actionLog.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="w-4 h-4 text-blue-600" />
                  <p className="text-blue-900 font-medium text-sm">
                    Session Actions ({actionLog.length})
                  </p>
                </div>
                <div className="space-y-1">
                  {actionLog.slice(-5).map((action, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-blue-700">
                      {action.action_type === 'approve_resolution' && <CheckCircle className="w-3 h-3 text-green-600" />}
                      {action.action_type === 'reject_resolution' && <XCircle className="w-3 h-3 text-red-600" />}
                      {action.action_type === 'add_note' && <MessageSquare className="w-3 h-3 text-blue-600" />}
                      {action.action_type === 'flag' && <Flag className="w-3 h-3 text-amber-600" />}
                      <span>
                        {action.action_type.replace('_', ' ')} on anomaly ...{action.anomaly_id.slice(-6)}
                        {action.note && ` — "${action.note.slice(0, 60)}${action.note.length > 60 ? '...' : ''}"`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AnomalyResolver Component - reused from wizard */}
            <AnomalyResolver
              anomalies={reviewData.anomalies.map((a: any) => ({
                id: a.id || a.anomaly_id || `anm-${Math.random().toString(36).slice(2, 8)}`,
                severity: a.severity || 'medium',
                type: a.anomaly_type || a.type || 'revenue_change',
                title: a.title || `${a.anomaly_type || 'Anomaly'} — ${a.affected_period || 'Unknown period'}`,
                description: a.description || a.explanation || '',
                percent: a.deviation_percent || a.percent || 0,
                from: a.from_value || a.from || 0,
                to: a.to_value || a.to || 0,
                periodFrom: a.period_from || a.periodFrom || '',
                periodTo: a.period_to || a.periodTo || '',
                period: a.affected_period || a.period || '',
                deviation: a.deviation_percent || a.deviation || 0,
                categories: a.category_breakdown || a.categories || [],
                transactions: a.top_transactions || a.transactions || [],
                aiSuggestion: a.ai_analysis?.explanation || a.aiSuggestion || a.explanation || '',
                aiConfidence: a.ai_analysis?.confidence || a.aiConfidence,
                sourceDoc: a.source_document || a.sourceDoc,
                lossEvent: a.is_loss_period || a.lossEvent,
                is_resolved: a.is_resolved || false,
                resolution_note: a.resolution_note || '',
                resolved_at: a.resolved_at || '',
                resolution_reason: a.resolution_reason || '',
                isPreLoss: a.is_pre_loss || a.isPreLoss,
                isLossPeriod: a.is_loss_period || a.isLossPeriod,
                isPostLoss: a.is_post_loss || a.isPostLoss,
                forensic_insight: a.forensic_insight,
                forensic_analysis: a.forensic_analysis,
                accountant_review: a.accountant_review,
              }))}
              periodsData={reviewData.periods_data}
              totalTransactions={reviewData.total_transactions}
              totalMonths={reviewData.total_months}
              lossEventStart={reviewData.loss_event_start}
              lossEventEnd={reviewData.loss_event_end}
              onResolve={handleResolve}
              onBatchResolve={handleBatchResolve}
              loading={false}
            />
          </div>
        ) : null}
      </div>
    </AccountantLayout>
  );
}
